import { Wallet, Send, ShoppingBag, TrendingUp, Plus } from "lucide-react";
import { reais, roi } from "./api";

/**
 * Os quatro indicadores da Visao geral, na ordem definida pelo dono:
 *
 *   MEU SALDO            -> valor em carteira + quantos envios ele compra
 *   SMS ENVIADOS         -> quantidade no periodo + quanto custou envia-los
 *   PEDIDOS INFLUENCIADOS-> pedidos vindos das campanhas + faturamento deles
 *                           (telefone de quem recebeu + link; o link sozinho na legenda)
 *   RECEITA POR SMS      -> faturamento / mensagens enviadas + ROI em "x"
 *
 * Toda conta em inteiros (centavos/centesimos); formatacao so' na borda.
 */
export default function KpisSMS({ carteira, envio, faturamento, onAdicionarSaldo, canal = "sms" }) {
  const ehVoz = canal === "voz";
  const saldoCentavos = carteira?.saldo_equivalente_centavos ?? 0;
  const saldoEnvios = carteira?.saldo_creditos ?? 0;
  const enviados = envio?.enviados ?? 0;
  const custo = faturamento?.custo_centavos ?? 0;
  const pedidos = faturamento?.pedidos ?? 0;
  const receita = faturamento?.receita_centavos ?? 0;
  const pedidosLink = faturamento?.pedidos_link ?? 0;
  // Receita media por mensagem enviada, em centavos inteiros.
  const receitaPorSms = enviados ? Math.trunc(receita / enviados) : null;
  const roiCent = faturamento?.roi_centesimos ?? null;

  // ROI sem casa decimal quando e' redondo: "30x", nao "30,0x".
  const roiCurto = roiCent === null ? null : roi(roiCent).replace(",0x", "x");

  // Formato definido pelo dono: cada card mostra o PAR "valor / valor" na
  // linha principal — o numero e o seu contexto juntos, sem legenda embaixo.
  const cards = [
    {
      rotulo: "Meu saldo",
      valor: reais(saldoCentavos),
      valor2: `${saldoEnvios.toLocaleString("pt-BR")} ${ehVoz ? "ligações" : "SMS"}`,
      legenda: ehVoz ? "saldo em carteira / ligações que ele paga"
                     : "saldo em carteira / envios que ele paga",
      borda: "border-t-emerald-500",
      icone: Wallet,
      corIcone: "text-emerald-500 bg-emerald-500/10",
      botao: { rotulo: "Adicionar saldo", acao: onAdicionarSaldo },
    },
    {
      rotulo: ehVoz ? "Ligações feitas" : "SMS enviados",
      valor: enviados.toLocaleString("pt-BR"),
      valor2: reais(custo),
      legenda: ehVoz ? "ligações feitas / valor investido nelas" : "mensagens enviadas / valor investido nelas",
      borda: "border-t-blue-500",
      icone: Send,
      corIcone: "text-blue-500 bg-blue-500/10",
    },
    {
      rotulo: "Pedidos influenciados",
      valor: pedidos.toLocaleString("pt-BR"),
      valor2: reais(receita),
      // Cruzamento nos dois canais: quem recebeu e pediu (+ o link, no SMS).
      legenda: ehVoz ? "pedidos de quem recebeu a ligação, até 7 dias depois / faturamento deles"
                     : `pedidos de quem recebeu ou comprou pelo link, até 7 dias depois / faturamento deles${
                         pedidosLink ? ` · ${pedidosLink.toLocaleString("pt-BR")} pelo link` : ""}`,
      borda: "border-t-violet-500",
      icone: ShoppingBag,
      corIcone: "text-violet-500 bg-violet-500/10",
    },
    {
      rotulo: ehVoz ? "Receita por ligação" : "Receita por SMS",
      valor: receitaPorSms === null ? "—" : reais(receitaPorSms),
      valor2: roiCurto === null ? "sem ROI ainda" : roiCurto,
      legenda: ehVoz ? "faturamento por ligação feita / retorno sobre o investido"
               : "faturamento por SMS enviado / retorno sobre o investido",
      borda: "border-t-amber-500",
      icone: TrendingUp,
      corIcone: "text-amber-500 bg-amber-500/10",
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {cards.map(({ rotulo, valor, valor2, legenda, borda, icone: Icone, corIcone, botao }) => (
        <div key={rotulo} className={`rounded-xl border border-t-2 ${borda} bg-card p-4`}>
          <div className="flex items-start justify-between gap-2">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{rotulo}</p>
            {botao ? (
              <button onClick={botao.acao}
                className="inline-flex items-center gap-1 rounded-lg bg-emerald-500/10 text-emerald-500 px-2.5 py-1 text-xs font-semibold hover:bg-emerald-500/20 transition-colors whitespace-nowrap">
                <Plus className="w-3.5 h-3.5" /> {botao.rotulo}
              </button>
            ) : (
              <span className={`rounded-lg p-1.5 ${corIcone}`}><Icone className="w-4 h-4" /></span>
            )}
          </div>
          <p className="text-2xl font-bold mt-2 tabular-nums">
            {valor}
            <span className="text-muted-foreground font-semibold"> / {valor2}</span>
          </p>
          <p className="text-[11px] text-muted-foreground mt-1">{legenda}</p>
        </div>
      ))}
    </div>
  );
}
